import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from './LoadingSpinner';
import { getFullImageUrl } from '@/lib/api/apiClient';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string | null) => void;
  onUpload: (file: File) => Promise<string>;
  placeholder?: string;
  maxSizeMB?: number;
  className?: string;
  /** Shape: 'rectangle' for banners (default), 'square' for logos/icons */
  shape?: 'rectangle' | 'square';
  /** Recommended size hint text */
  sizeHint?: string;
}

export const ImageUpload = ({
  value,
  onChange,
  onUpload,
  placeholder = 'اختر صورة للرفع',
  maxSizeMB = 5,
  className = '',
  shape = 'rectangle',
  sizeHint,
}: ImageUploadProps) => {
  const isSquare = shape === 'square';
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    const maxSize = maxSizeMB * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (file.size > maxSize) {
      toast.error(`حجم الملف يجب أن يكون أقل من ${maxSizeMB} ميجابايت`);
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('نوع الملف غير مدعوم. الأنواع المدعومة: JPG, PNG, GIF, WebP');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (file: File) => {
    if (!validateFile(file)) return;

    try {
      setUploading(true);
      const url = await onUpload(file);
      onChange(url);
      toast.success('تم رفع الصورة بنجاح');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleInputChange}
        className="hidden"
      />

      {value ? (
        // Show uploaded image
        <div className={`relative group ${isSquare ? 'w-40 mx-auto' : 'w-full'}`}>
          <div className={`
            ${isSquare ? 'w-40 h-40' : 'w-full h-48'} 
            ${isSquare ? 'rounded-2xl' : 'rounded-lg'} 
            overflow-hidden border-2 border-accent-greenLight/30 bg-gray-50
          `}>
            <img
              src={getFullImageUrl(value) || value}
              alt="Uploaded"
              className={`w-full h-full ${isSquare ? 'object-contain p-2' : 'object-cover'}`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className={`
            absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
            transition-opacity flex items-center justify-center gap-2
            ${isSquare ? 'rounded-2xl' : 'rounded-lg'}
          `}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <LoadingSpinner size="sm" /> : 'تغيير'}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        // Show upload area
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            ${isSquare ? 'w-40 h-40 mx-auto rounded-2xl' : 'w-full h-48 rounded-lg'} 
            border-2 border-dashed cursor-pointer
            flex flex-col items-center justify-center gap-2
            transition-all duration-200
            ${dragActive
              ? 'border-secondary bg-secondary/10'
              : 'border-accent-greenLight/30 hover:border-secondary hover:bg-accent-greenLight/5'
            }
          `}
        >
          {uploading ? (
            <>
              <LoadingSpinner />
              <span className="text-xs text-accent-dark/70">جاري الرفع...</span>
            </>
          ) : (
            <>
              <div className={`${isSquare ? 'p-2' : 'p-3'} bg-accent-greenLight/20 rounded-full`}>
                {dragActive ? (
                  <Upload className={`${isSquare ? 'w-6 h-6' : 'w-8 h-8'} text-secondary`} />
                ) : (
                  <ImageIcon className={`${isSquare ? 'w-6 h-6' : 'w-8 h-8'} text-accent-greenDark`} />
                )}
              </div>
              <div className="text-center px-2">
                <p className={`${isSquare ? 'text-xs' : 'text-sm'} font-medium text-accent-dark`}>
                  {dragActive ? 'أفلت الصورة هنا' : placeholder}
                </p>
                {!isSquare && (
                  <p className="text-xs text-accent-dark/50 mt-1">
                    أو اسحب الصورة وأفلتها هنا
                  </p>
                )}
                <p className="text-xs text-accent-dark/40 mt-1">
                  {sizeHint || `PNG, JPG (حد ${maxSizeMB}MB)`}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
